import { isEmpty } from 'lodash/lang';

import {
  isExpenseEventType,
  isParentEventType,
  isParentPDF
} from 'components/pages/finance/NewInvoice/services/service';

import { toFormattedFinanceBill } from '../../../../../../services/financial';
import { eventTypeLabel } from '../../../../setup/Budget/BudgetDetails/budgetConstant';

export const MAX_ITEMS_PER_PAGE = 25;
const MAX_PAGES_SAFETY = 20000;
const REIMBURSEMENT_ITEM = 'REIMBURSEMENT_ITEM';

export function findMaxIndexThatFits(heights, height) {
  const lastIndex = heights.length - 1;
  let sum = 0;
  for (let i = 0; i <= lastIndex; ++i) {
    sum += heights[i];
    if (sum > height) {
      return i - 1;
    }
  }
  return lastIndex;
}

export function cutEventsForPages(groups, maxIndex) {
  if (groups[maxIndex]?.typeHeader) {
    return { currentPage: groups.slice(0, maxIndex), nextPage: groups.slice(maxIndex) };
  }
  if (groups[maxIndex]?.typeSubHeader) {
    const normalizedMaxIndex = groups[maxIndex - 1]?.typeHeader ? maxIndex - 1 : maxIndex;
    return { currentPage: groups.slice(0, normalizedMaxIndex), nextPage: groups.slice(normalizedMaxIndex) };
  }
  if (
    groups[maxIndex]?.budgetEventType === eventTypeLabel.PATIENT_REIMBURSEMENT &&
    groups[maxIndex]?.templateRowType !== REIMBURSEMENT_ITEM &&
    !groups[maxIndex]?.reimbursementEventSuffix
  ) {
    const normalizedMaxIndex = groups[maxIndex - 2]?.typeHeader ? maxIndex - 2 : maxIndex - 1;
    return {
      currentPage: groups.slice(0, normalizedMaxIndex),
      nextPage: groups.slice(normalizedMaxIndex)
    };
  }
  return { currentPage: groups.slice(0, maxIndex + 1), nextPage: groups.slice(maxIndex + 1) };
}

export function getQuantityAndAmountForGroup(element, itemsForPage, invoiceType) {
  if (element?.subGroupName) {
    const subGroups = itemsForPage.filter(
      budgetEvent => budgetEvent.groupName === element.groupName && budgetEvent?.typeSubHeader
    );
    const subElements = itemsForPage.filter(
      budgetEvent =>
        budgetEvent.subGroupName === element.subGroupName &&
        budgetEvent.groupName === element.groupName &&
        !budgetEvent?.typeSubHeader &&
        budgetEvent.templateRowType !== REIMBURSEMENT_ITEM
    );
    const groupLength = subGroups.length;
    const groupAmount = subGroups.reduce((acc, item) => {
      if (isParentEventType(item.invoiceType)) {
        return acc + item.invoiceAmount;
      }
      return acc;
    }, 0);
    const subGroupAmount = subElements.reduce((acc, item) => {
      if (isParentEventType(item.invoiceType)) {
        return acc + item.invoiceAmount;
      }
      return acc;
    }, 0);
    return { subGroupAmount, groupLength, groupAmount };
  }

  const groupLength = itemsForPage
    .filter(budgetEvent => {
      if (isParentPDF(invoiceType)) {
        return isParentEventType(budgetEvent.invoiceType);
      }
      return true;
    })
    .filter(budgetEvent => {
      if (isExpenseEventType(budgetEvent.budgetEventType)) {
        return budgetEvent.groupName === element.groupName;
      }
      return budgetEvent.groupName === element.groupName && budgetEvent.invoiceAmount === element.invoiceAmount;
    }).length;

  const groupRateForFixedAndVariableEvents = toFormattedFinanceBill(element.invoiceAmount);

  return {
    subGroupCount: null,
    subGroupAmount: null,
    groupLength,
    groupAmount: null,
    groupRateForFixedAndVariableEvents
  };
}

function rewriteCurrentPageTotals(currentPage, invoiceType) {
  if (currentPage.length === 0) return currentPage;
  const lastElement = currentPage[currentPage.length - 1];

  const withNormalizedInvoiceAmounts = currentPage.map(budgetEvent => {
    if (
      budgetEvent.budgetEventType === eventTypeLabel.PATIENT_REIMBURSEMENT &&
      budgetEvent.groupName === lastElement.groupName &&
      budgetEvent.subGroupName === lastElement.subGroupName &&
      budgetEvent.id === lastElement.id &&
      lastElement.templateRowType === REIMBURSEMENT_ITEM &&
      budgetEvent.reimbursementEventSuffix === '(cont.)'
    ) {
      return { ...budgetEvent, invoiceAmount: 0 };
    }
    return budgetEvent;
  });

  const withNormalizedSubHeaders = withNormalizedInvoiceAmounts.map(budgetEvent => {
    if (
      budgetEvent?.typeSubHeader &&
      budgetEvent.subGroupName === lastElement.subGroupName &&
      budgetEvent.groupName === lastElement.groupName
    ) {
      const { subGroupAmount } = getQuantityAndAmountForGroup(lastElement, withNormalizedInvoiceAmounts, invoiceType);
      return { ...budgetEvent, subGroupAmount };
    }
    return budgetEvent;
  });

  return withNormalizedSubHeaders.map(budgetEvent => {
    if (budgetEvent?.typeHeader && budgetEvent.groupName === lastElement.groupName && !budgetEvent.inseparable) {
      const { groupAmount, groupLength } = getQuantityAndAmountForGroup(
        lastElement,
        withNormalizedSubHeaders,
        invoiceType
      );
      return { ...budgetEvent, groupAmount, groupLength };
    }
    return budgetEvent;
  });
}

function buildNextPagePrepend(tail, invoiceType) {
  if (isEmpty(tail)) return { syntheticRows: [], mutatedFirstRow: null };
  const firstElement = tail[0];
  if (firstElement.typeHeader || firstElement.typeBalance) {
    return { syntheticRows: [], mutatedFirstRow: null };
  }

  const { subGroupAmount, groupLength, groupRateForFixedAndVariableEvents } = getQuantityAndAmountForGroup(
    firstElement,
    tail,
    invoiceType
  );

  if (firstElement.subGroupName) {
    if (firstElement.typeSubHeader) {
      return {
        syntheticRows: [
          {
            typeHeader: true,
            inseparable: true,
            groupName: firstElement.groupName,
            groupHeaderSuffix: '(cont.)'
          }
        ],
        mutatedFirstRow: null
      };
    }
    if (firstElement.templateRowType === REIMBURSEMENT_ITEM) {
      return {
        syntheticRows: [
          {
            typeHeader: true,
            inseparable: true,
            groupName: firstElement.groupName,
            groupHeaderSuffix: '(cont.)'
          },
          {
            typeSubHeader: true,
            groupName: firstElement.groupName,
            subGroupName: firstElement.subGroupName,
            subGroupAmount
          },
          {
            typeSubItem: true,
            groupName: firstElement.groupName,
            subGroupName: firstElement.subGroupName,
            invoiceType: firstElement.invoiceType,
            displayName: firstElement.displayName || firstElement.name,
            reimbursementEventSuffix: '(cont.)'
          }
        ],
        mutatedFirstRow: null
      };
    }
    return {
      syntheticRows: [
        {
          typeHeader: true,
          inseparable: true,
          groupName: firstElement.groupName,
          groupHeaderSuffix: '(cont.)'
        },
        {
          typeSubHeader: true,
          groupName: firstElement.groupName,
          subGroupName: firstElement.subGroupName,
          subGroupAmount
        }
      ],
      mutatedFirstRow: null
    };
  }

  return {
    syntheticRows: [
      {
        typeHeader: true,
        groupName: firstElement.groupName,
        groupHeaderSuffix: '(cont.)',
        groupLength,
        amountForItem: firstElement.invoiceAmount,
        headerBudgetEventType: firstElement.budgetEventType,
        groupRateForFixedAndVariableEvents,
        groupNameForFixedAndVariableEvents: firstElement.groupNameForFixedAndVariableEvents
      }
    ],
    mutatedFirstRow: null
  };
}

function splitWindow(windowRows, windowHeights, heightBudget) {
  let maxIndex = findMaxIndexThatFits(windowHeights, heightBudget);
  if (maxIndex < 0) {
    maxIndex = 0;
  }
  const { currentPage, nextPage } = cutEventsForPages(windowRows, maxIndex);
  if (currentPage.length === 0) {
    return { currentPage: windowRows.slice(0, 1), nextPage: windowRows.slice(1) };
  }
  return { currentPage, nextPage };
}

export function paginateInvoice({
  normalizedGroups,
  rowHeights,
  bodyHeight,
  continuationBodyHeight,
  representativeHeights,
  invoiceType,
  bankInfoHeight = 0,
  headerHeightByGroup,
  subHeaderHeightByGroup
}) {
  if (!Array.isArray(normalizedGroups) || normalizedGroups.length === 0) {
    return [];
  }
  if (!Number.isFinite(bodyHeight) || bodyHeight <= 0) {
    return [normalizedGroups];
  }
  const resolvedContinuationBodyHeight =
    Number.isFinite(continuationBodyHeight) && continuationBodyHeight > 0 ? continuationBodyHeight : bodyHeight;

  const { headerHeight = 0, subHeaderHeight = 0 } = representativeHeights || {};

  let remaining = normalizedGroups.map((row, i) => ({
    row,
    height: rowHeights[i] ?? 0
  }));

  const pages = [];

  while (remaining.length > 0 && pages.length < MAX_PAGES_SAFETY) {
    const pageBudget = pages.length === 0 ? bodyHeight : resolvedContinuationBodyHeight;
    const windowEntries = remaining.slice(0, MAX_ITEMS_PER_PAGE);
    const windowHeights = windowEntries.map(e => e.height);
    const windowRows = windowEntries.map(e => e.row);

    let { currentPage: effectiveCurrentPage, nextPage: effectiveNextPage } = splitWindow(
      windowRows,
      windowHeights,
      pageBudget
    );

    let forcedLength = effectiveCurrentPage.length;
    while (
      forcedLength < windowEntries.length &&
      windowEntries.slice(0, forcedLength).every(entry => entry.synthetic)
    ) {
      forcedLength += 1;
    }
    if (forcedLength !== effectiveCurrentPage.length) {
      effectiveCurrentPage = windowRows.slice(0, forcedLength);
      effectiveNextPage = windowRows.slice(forcedLength);
    }

    const isCandidateFinalPage = effectiveNextPage.length === 0 && remaining.length <= MAX_ITEMS_PER_PAGE;
    if (isCandidateFinalPage && bankInfoHeight > 0 && effectiveCurrentPage.length > 1) {
      const lastIndex = effectiveCurrentPage.length - 1;
      const lastRow = effectiveCurrentPage[lastIndex];
      if (lastRow?.typeBalance) {
        const consumedHeight = windowHeights
          .slice(0, effectiveCurrentPage.length)
          .reduce((sum, height) => sum + height, 0);
        if (consumedHeight > pageBudget - bankInfoHeight) {
          effectiveCurrentPage = effectiveCurrentPage.slice(0, lastIndex);
          effectiveNextPage = [lastRow, ...effectiveNextPage];
        }
      }
    }

    const consumed = effectiveCurrentPage.length;

    pages.push(rewriteCurrentPageTotals(effectiveCurrentPage, invoiceType));

    const tailEntries = remaining.slice(consumed);
    const tailRows = tailEntries.map(e => e.row);

    const { syntheticRows, mutatedFirstRow } = buildNextPagePrepend(tailRows, invoiceType);

    let newRemaining = tailEntries;
    if (mutatedFirstRow && newRemaining.length > 0) {
      newRemaining = [{ row: mutatedFirstRow, height: newRemaining[0].height }, ...newRemaining.slice(1)];
    }
    if (syntheticRows.length > 0) {
      const syntheticEntries = syntheticRows.map(row => ({
        row,
        height: row.typeHeader
          ? headerHeightByGroup?.get(row.groupName) ?? headerHeight
          : subHeaderHeightByGroup?.get(row.subGroupName) ?? subHeaderHeight,
        synthetic: true
      }));
      newRemaining = [...syntheticEntries, ...newRemaining];
    }

    remaining = newRemaining;

    if (effectiveNextPage.length === 0 && remaining.length === 0) {
      break;
    }
  }

  return pages;
}
